import '@townsquare/logging/instrument';

import { default as FeatureGateClient } from '@atlaskit/feature-gate-js-client';
import React from 'react';
import ReactDOM from 'react-dom';
import { createRoot } from 'react-dom/client';
import { matchRoute } from 'react-resource-router';

import { prefetchedAuthCheckPromise } from '@townsquare/auth/prefetched-auth-check';
import { getLogInUrl } from '@townsquare/auth-utils';
import { getConfig, getPlatformEnvironment } from '@townsquare/config';
import { location, redirect } from '@townsquare/facade/window';
import type { UserResponse } from '@townsquare/identity-client';
import { isEmbedRoute } from '@townsquare/router/primitives/match-route';
import { ORG_SITE_ROUTE_REGEX, isGoalsAppRoute, isProjectsAppRoute, isTeamsAppRoute } from '@townsquare/router/utils';
import { createSearchParamsFromCurrentUrl } from '@townsquare/search-params';
import { fg } from '@townsquare/stat-sig/gate';
import { featureGatesOptions } from '@townsquare/stat-sig/options';

import { GoalsApp } from './app-entries/Goals';
import { HomeApp } from './app-entries/Home';
import { ProjectsApp } from './app-entries/Projects';
import { TeamsApp } from './app-entries/Teams';
import { routes } from './router/routes';
import { getSiteIdentifier } from './utils';

const container = document.getElementById('root');
const config = getConfig();
const hacUrl = config.fullUrl;

type HandleAuthResponse = { isRedirecting: false; user: UserResponse } | { isRedirecting: true; user: null };

async function handleAuth(): Promise<HandleAuthResponse> {
	try {
		const { isAuthed, isUnauthenticated, isUnauthorised, isNoUser, user } = await prefetchedAuthCheckPromise;

		if (isAuthed) {
			// User is authenticated
			if (user) {
				return { isRedirecting: false, user };
			} else {
				redirect(getLogInUrl());
			}
		} else if (isUnauthorised) {
			const redirectUrl = new URL(hacUrl);
			if (isNoUser && location().pathname !== redirectUrl.pathname) {
				redirect(hacUrl);
			}
		} else if (isUnauthenticated) {
			redirect(getLogInUrl());
		} else {
			// Error code other than 401
			redirect(hacUrl);
		}

		return { isRedirecting: true, user: null };
	} catch (e) {
		redirect(hacUrl);
		return { isRedirecting: true, user: null };
	}
}

function reactRender(node: React.ReactNode) {
	if (fg('townsquare_react18_render')) {
		const root = createRoot(container!);
		root.render(node);
	} else {
		ReactDOM.render(node as Parameters<typeof ReactDOM.render>[0], container);
	}
}

function renderHome() {
	reactRender(<HomeApp />);
}

function redirectToStudioApp() {
	const isProd = getPlatformEnvironment() === 'prod';
	const redirectBaseUrl = isProd
		? 'https://studio.atlassian.com'
		: 'https://atlassian-studio.stg-east.frontend.public.atl-paas.net';
	redirect(redirectBaseUrl + location().pathname.replace('/studio', '') + location().search);
}

async function render() {
	const urlSearch = Object.fromEntries(createSearchParamsFromCurrentUrl());
	const matchedRoute = matchRoute(routes, window.location.pathname, urlSearch);

	/**
	 * If we are trying to load an embed, we want to provide a 'pass-through'
	 * As we have special handling in place to deal with third-party cookies & iframe login.
	 */
	if (isEmbedRoute(matchedRoute?.route)) {
		return renderHome();
	}

	const { isRedirecting, user } = await handleAuth();

	if (isRedirecting) {
		return;
	}

	try {
		await FeatureGateClient.initialize(featureGatesOptions, {
			// Need at least one identifier to init statsig
			atlassianAccountId: user.account_id,
			tenantId: getSiteIdentifier() ?? undefined,
		});
	} catch (e) {
		//
	}

	const pathname = location().pathname.replace(ORG_SITE_ROUTE_REGEX, '');
	if (isGoalsAppRoute(pathname)) {
		await FeatureGateClient.updateUser(
			featureGatesOptions,
			{
				atlassianAccountId: user.account_id,
				tenantId: getSiteIdentifier() ?? undefined,
			},
			{
				targetApp: 'atlas_web',
				townsquareApp: 'goals',
			},
		);
		reactRender(<GoalsApp />);
	} else if (isProjectsAppRoute(pathname)) {
		await FeatureGateClient.updateUser(
			featureGatesOptions,
			{
				atlassianAccountId: user.account_id,
				tenantId: getSiteIdentifier() ?? undefined,
			},
			{
				targetApp: 'atlas_web',
				townsquareApp: 'projects',
			},
		);
		reactRender(<ProjectsApp />);
	} else if (isTeamsAppRoute(pathname)) {
		await FeatureGateClient.updateUser(
			featureGatesOptions,
			{
				atlassianAccountId: user.account_id,
				tenantId: getSiteIdentifier() ?? undefined,
			},
			{
				targetApp: 'atlas_web',
				townsquareApp: 'teams',
			},
		);
		reactRender(<TeamsApp />);
	} else if (pathname.startsWith('/studio')) {
		// redirect from legacy studio app to new studio app in case user has the old URL
		return redirectToStudioApp();
	} else {
		renderHome();
	}
}

render();
